import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Flex,
  Heading,
  ProgressCircle,
  Text,
  VisuallyHidden,
} from '@/components/ui';
import {
  IconCheck,
  IconCircleCheckFilled,
  IconCloudUpload,
  IconExclamationCircle,
  IconFiles,
  IconLoader2,
  IconReload,
  IconSchema,
  IconTable,
} from '@tabler/icons-react';
import { WizardStep, WizardStepStatus } from './types';

interface StudyDataImportProgressBarProps {
  currentStep: WizardStep;
  stepStatuses: Record<WizardStep, WizardStepStatus>;
  onStartNew?: () => void;
  readyToImportCount?: number;
  totalFilesCount?: number;
}

const WIZARD_STEPS: Array<{
  id: WizardStep;
  label: string;
  icon: React.ComponentType<{ size?: number; style?: React.CSSProperties }>;
  description: string;
}> = [
  {
    id: 'file-upload',
    label: 'File Upload',
    icon: IconFiles,
    description: 'Select CSV files for import',
  },
  {
    id: 'form-detection',
    label: 'Form Detection',
    icon: IconSchema,
    description: 'Map files to EDC forms and columns',
  },
  {
    id: 'preview-and-validation',
    label: 'Preview & Validation',
    icon: IconTable,
    description: 'Review forms and validate data',
  },
  {
    id: 'import',
    label: 'Import',
    icon: IconCloudUpload,
    description: 'Execute the import process',
  },
];

export const StudyDataImportProgressBar: React.FC<
  StudyDataImportProgressBarProps
> = ({
  currentStep,
  stepStatuses,
  onStartNew,
  readyToImportCount,
  totalFilesCount,
}) => {
  const currentStepIndex = WIZARD_STEPS.findIndex(
    step => step.id === currentStep
  );

  const getStepStatus = (
    stepId: WizardStep,
    index: number
  ): WizardStepStatus => {
    return (
      stepStatuses[stepId] ||
      (index < currentStepIndex
        ? 'completed'
        : index === currentStepIndex
          ? 'current'
          : 'pending')
    );
  };

  const getStepIcon = (
    step: {
      id: WizardStep;
      icon: React.ComponentType<{ size?: number; style?: React.CSSProperties }>;
    },
    status: WizardStepStatus
  ) => {
    // Special handling for form-detection step with progress
    if (
      step.id === 'form-detection' &&
      readyToImportCount !== undefined &&
      totalFilesCount !== undefined &&
      totalFilesCount > 0
    ) {
      const percentage = (readyToImportCount / totalFilesCount) * 100;

      // Show CircleCheckFilled when 100% complete
      if (percentage === 100) {
        return (
          <IconCircleCheckFilled
            size={16}
            style={{ color: 'var(--green-9)' }}
          />
        );
      }

      // Show ProgressCircle for partial progress
      return (
        <ProgressCircle
          value={readyToImportCount}
          max={totalFilesCount}
          size="xsmall"
          color="primary"
          showValue={false}
          strokeWidthValue={5}
        >
          <VisuallyHidden>
            {readyToImportCount}/{totalFilesCount}
          </VisuallyHidden>
        </ProgressCircle>
      );
    }

    // Default icon logic for other steps
    const IconComponent = step.icon;
    const iconSize = 16;

    if (status === 'processing') {
      return (
        <IconLoader2
          size={iconSize}
          style={{
            color: 'var(--blue-9)',
            animation: 'spin 1s linear infinite',
          }}
        />
      );
    }

    if (status === 'completed') {
      return <IconCheck size={iconSize} style={{ color: 'var(--green-9)' }} />;
    }

    if (status === 'error') {
      return (
        <IconExclamationCircle
          size={iconSize}
          style={{ color: 'var(--red-9)' }}
        />
      );
    }

    return (
      <IconComponent
        size={iconSize}
        style={{
          color: status === 'current' ? 'var(--blue-9)' : 'var(--gray-9)',
        }}
      />
    );
  };

  return (
    /*<Card
      // mb="4"
      size="small"
      className="import-progress-card"
      style={{
        backgroundColor: 'var(--gray-2)',
        flexShrink: 0,
      }}
    >*/
    <Flex direction="row" gap="3" align="center" justify="between">
      <Heading highContrast size="2xsmall" mr="4">
        Import Progress
      </Heading>

      <Flex align="center" gap="4" style={{ overflowX: 'auto' }}>
        {WIZARD_STEPS.map((step, index) => {
          const status = getStepStatus(step.id, index);
          const isLast = index === WIZARD_STEPS.length - 1;

          return (
            <React.Fragment key={step.id}>
              <Flex align="center" gap="2" style={{ minWidth: 'fit-content' }}>
                <Avatar
                  size="small"
                  variant="soft"
                  color={
                    status === 'completed'
                      ? 'success'
                      : status === 'error'
                        ? 'error'
                        : status === 'processing'
                          ? 'indigo'
                          : status === 'current'
                            ? 'primary'
                            : 'default'
                  }
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {getStepIcon(step, status)}
                </Avatar>
                <Text
                  size="2xsmall"
                  align="center"
                  weight={
                    status === 'current' || status === 'processing'
                      ? 'medium'
                      : 'regular'
                  }
                  color={
                    status === 'completed'
                      ? 'success'
                      : status === 'error'
                        ? 'error'
                        : status === 'current' || status === 'processing'
                          ? 'primary'
                          : 'gray'
                  }
                >
                  {step.label}
                </Text>
              </Flex>

              {!isLast && (
                <Box
                  style={{
                    width: 16,
                    height: 1,
                    backgroundColor:
                      index < currentStepIndex ||
                      (status === 'completed' &&
                        getStepStatus(WIZARD_STEPS[index + 1].id, index + 1) !==
                          'pending')
                        ? 'var(--green-7)'
                        : 'var(--gray-7)',
                    margin: '0 4px',
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </Flex>

      {/* Start New button */}
      {onStartNew && (
        <Button
          variant="outlined"
          size="small"
          onClick={onStartNew}
          startIcon={<IconReload />}
        >
          Start New
        </Button>
      )}
    </Flex>
    // </Card>
  );
};
